"use client";

/**
 * The one client-side path every submit form goes through. Rules:
 *  - Block only when there is genuinely nothing to submit (no venue name, an empty
 *    report). Anything else — a link we can't parse, a bare note with no source — is
 *    sent as-is with the visitor's raw input, and the server routes it to a human.
 *  - Every block and every failed POST is reported to /api/submission-failures with
 *    what the visitor typed, so no attempt ever disappears without a trace.
 */
import {
  captureSubmissionBlocked,
  captureSubmissionFailed,
} from "@/lib/observability/track";
import type { SubmissionPayload } from "./payload";
import { parseEvidenceGps, type EvidenceGps } from "./evidenceGps";
import { sanitizeRawInput, type RawInput } from "./rawInput";

/** A stable anonymous id kept in localStorage (PRD §1.1 — no accounts). */
export function getFingerprint(): string {
  try {
    const key = "hhf_fp";
    let v = localStorage.getItem(key);
    if (!v) {
      v = crypto.randomUUID();
      localStorage.setItem(key, v);
    }
    return v;
  } catch {
    return crypto.randomUUID();
  }
}

export interface FailureContext {
  /** Form id, e.g. "new_venue", "contribute", "report_closed", "contact". */
  form: string;
  targetType?: string;
  targetId?: string | null;
  rawInput?: RawInput;
}

function currentPath(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return `${window.location.pathname}${window.location.search}`;
}

function filledFields(raw?: RawInput): string[] {
  return raw ? Object.keys(raw).filter((k) => (raw[k] ?? "").trim() !== "") : [];
}

/** Fire-and-forget: write the failed attempt to our own log. Never throws. */
export function logFailure(ctx: FailureContext, reason: string, message?: string): void {
  try {
    const body = JSON.stringify({
      form: ctx.form,
      reason,
      message,
      targetType: ctx.targetType,
      targetId: ctx.targetId ?? undefined,
      // Same per-field/total caps as the server, so a long note can't push the report
      // past the endpoint's size limit (or the browser's 64 KB keepalive budget).
      rawInput: sanitizeRawInput(ctx.rawInput) ?? undefined,
      pagePath: currentPath(),
    });
    // keepalive lets the report finish even if the visitor closes the tab right away.
    void fetch("/api/submission-failures", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: new Blob([body]).size < 60_000,
    }).catch(() => {});
  } catch {
    /* never let logging break the form */
  }
}

/**
 * The form is stopping the visitor (nothing to submit, unusable file). Logs it, and
 * returns the message so the caller can `setError(block(...))`.
 */
export function block(ctx: FailureContext, reason: string, message: string): string {
  logFailure(ctx, reason, message);
  captureSubmissionBlocked({ form: ctx.form, reason, message, filled_fields: filledFields(ctx.rawInput) });
  return message;
}

export type SubmitResult =
  | { ok: true; statusUrl: string | null; id: string | null }
  | { ok: false; error: string };

/**
 * POST to an API route with a timeout. Server rejections are logged server-side (with
 * the raw input we send); network errors and timeouts never reach the server, so they're
 * logged from here.
 */
/** What the visitor is waiting on right now — drives the status line under the button. */
export type SubmitProgress =
  | { phase: "uploading"; pct: number; bytes: number }
  | { phase: "saving" };

interface RawResponse {
  status: number;
  data: { error?: string; statusUrl?: string; id?: string };
}

/**
 * XHR rather than fetch: fetch can't report upload progress, and a menu photo on a phone
 * connection is exactly the multi-second wait the visitor needs to see moving.
 */
function send(
  url: string,
  json: string,
  onProgress?: (p: SubmitProgress) => void,
): Promise<RawResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.timeout = 60_000;
    const bytes = new Blob([json]).size;
    if (onProgress) {
      onProgress(bytes > 200_000 ? { phase: "uploading", pct: 0, bytes } : { phase: "saving" });
      xhr.upload.onprogress = (e) => {
        if (bytes > 200_000 && e.lengthComputable) {
          onProgress({ phase: "uploading", pct: Math.round((e.loaded / e.total) * 100), bytes });
        }
      };
      // Upload done — now the server is checking the photo and saving.
      xhr.upload.onload = () => onProgress({ phase: "saving" });
    }
    xhr.onload = () => {
      let data: RawResponse["data"] = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* non-JSON error page (proxy 502, etc.) */
      }
      resolve({ status: xhr.status, data });
    };
    xhr.onerror = () => reject(Object.assign(new Error("network error"), { name: "NetworkError" }));
    xhr.onabort = () => reject(Object.assign(new Error("aborted"), { name: "AbortError" }));
    xhr.ontimeout = () => reject(Object.assign(new Error("timed out"), { name: "TimeoutError" }));
    xhr.send(json);
  });
}

/**
 * POST to an API route with a timeout and optional progress. Server rejections are logged
 * server-side (with the raw input we send); network errors and timeouts never reach the
 * server, so they're logged from here.
 */
export async function postJson(
  url: string,
  body: unknown,
  ctx: FailureContext,
  onProgress?: (p: SubmitProgress) => void,
): Promise<SubmitResult> {
  try {
    const { status, data } = await send(url, JSON.stringify(body), onProgress);
    if (status < 200 || status >= 300) {
      const error =
        data.error ??
        (status >= 500 || status === 0
          ? "Our server hit a snag — your info is still here, try again in a moment."
          : "Something went wrong — please try again.");
      captureSubmissionFailed({
        target_type: ctx.targetType ?? ctx.form,
        form: ctx.form,
        reason: "http_error",
        status,
        message: data.error,
      });
      // A 5xx / 413 / HTML error page either never reached our handler's logging or was
      // rejected before the body (and its raw input) could be read — log it from here.
      if (status >= 500 || status === 413 || !data.error) {
        logFailure(ctx, `http_${status}`, error);
      }
      return { ok: false, error };
    }
    return { ok: true, statusUrl: data.statusUrl ?? null, id: data.id ?? null };
  } catch (err) {
    const timedOut = err instanceof Error && err.name === "TimeoutError";
    const error = timedOut
      ? "That took too long — your info is still here, try again."
      : "Network error — your info is still here, try again.";
    captureSubmissionFailed(
      {
        target_type: ctx.targetType ?? ctx.form,
        form: ctx.form,
        reason: timedOut ? "timeout" : "network_error",
        message: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
      },
      err,
    );
    logFailure(ctx, timedOut ? "timeout" : "network_error", error);
    return { ok: false, error };
  }
}

/** The status line shown while a submission is in flight. */
export function progressText(p: SubmitProgress | null, hasPhoto: boolean): string {
  if (!p) return "Checking you're not a bot…";
  if (p.phase === "uploading") {
    const mb = (p.bytes / (1024 * 1024)).toFixed(1);
    return p.pct < 100
      ? `Uploading your ${hasPhoto ? "photo" : "update"} (${mb} MB) — ${p.pct}%`
      : "Upload done — saving…";
  }
  return hasPhoto ? "Checking your photo and saving — a few seconds…" : "Saving…";
}

/** POST a submission (adds the raw input + form id the server logs and stores). */
export function postSubmission(
  payload: SubmissionPayload,
  ctx: FailureContext,
  onProgress?: (p: SubmitProgress) => void,
): Promise<SubmitResult> {
  return postJson(
    "/api/submissions",
    { ...payload, form: ctx.form, rawInput: ctx.rawInput },
    ctx,
    onProgress,
  );
}

/* ---------------------------------------------------------------------------------- */
/* Evidence files                                                                      */
/* ---------------------------------------------------------------------------------- */

/** Server cap is 6 MB decoded; aim under it with room to spare. */
const TARGET_BYTES = 5 * 1024 * 1024;
/**
 * Photos above this are re-encoded before upload. A phone's 3–5 MB menu photo becomes
 * ~0.5–1 MB at 2000 px — still sharp enough to read every price, and several seconds
 * less upload on a mobile connection.
 */
const SHRINK_ABOVE_BYTES = 1.5 * 1024 * 1024;
const MAX_EDGE = 2000;

export type PreparedFile =
  | { ok: true; dataUrl: string; name: string; gps: EvidenceGps | null }
  | { ok: false; reason: "file_type" | "file_too_large" | "file_unreadable"; error: string };

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => (typeof r.result === "string" ? resolve(r.result) : reject(new Error("empty")));
    r.onerror = () => reject(r.error ?? new Error("read failed"));
    r.readAsDataURL(file);
  });
}

/** Formats the server's image pipeline reads as-is. Anything else gets re-encoded here. */
const SAFE_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"]);

function typeFromName(name: string): string {
  const ext = /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase();
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "png" || ext === "gif" || ext === "webp" || ext === "heic" || ext === "heif") return `image/${ext}`;
  if (ext === "pdf") return "application/pdf";
  return "";
}

/** Read before any downscale — the canvas re-encode drops EXIF. Never blocks an upload. */
async function readGps(file: File): Promise<EvidenceGps | null> {
  try {
    const { default: exifr } = await import("exifr");
    const g = await exifr.gps(file);
    return parseEvidenceGps(g ? { lat: g.latitude, lng: g.longitude } : null);
  } catch {
    return null;
  }
}

async function downscale(file: File): Promise<Blob | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const g = canvas.getContext("2d");
    if (!g) return null;
    g.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    for (const q of [0.85, 0.7, 0.55]) {
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", q));
      if (blob && blob.size <= TARGET_BYTES) return blob;
    }
    return null;
  } catch {
    return null; // e.g. HEIC on a browser that can't decode it
  }
}

/**
 * Turn a picked file into a data URL the API accepts. Big phone photos are shrunk in the
 * browser instead of refused (a 12 MP photo is routinely 6–10 MB). Only a non-image or a
 * file that is too big AND can't be shrunk is rejected.
 */
export async function prepareEvidenceFile(file: File): Promise<PreparedFile> {
  // Mobile pickers often report an EMPTY type — fall back to the extension. The type
  // matters beyond this check: FileReader labels an untyped file
  // application/octet-stream, which the server silently drops.
  const type = file.type || typeFromName(file.name);
  const isPdf = type === "application/pdf";
  const isImage = type.startsWith("image/");
  if (!isPdf && !isImage) {
    return { ok: false, reason: "file_type", error: "That file isn't a photo or PDF — try a picture of the menu." };
  }
  try {
    const typed = file.type ? file : new File([file], file.name, { type });
    const gps = isImage ? await readGps(typed) : null;
    // HEIC/HEIF (iPhone) and anything over the size target: re-encode to JPEG in the
    // browser when it can decode it (Safari can decode HEIC; most others can't).
    if (isImage && (!SAFE_IMAGE_TYPES.has(type) || file.size > SHRINK_ABOVE_BYTES)) {
      const small = await downscale(typed);
      if (small) return { ok: true, dataUrl: await readAsDataUrl(small), name: file.name, gps };
    }
    if (file.size <= TARGET_BYTES) return { ok: true, dataUrl: await readAsDataUrl(typed), name: file.name, gps };
    return {
      ok: false,
      reason: "file_too_large",
      error: isPdf
        ? "That PDF is over 5 MB — try a screenshot of the happy-hour page instead."
        : "We couldn't shrink that photo enough — try a screenshot of it instead.",
    };
  } catch {
    return { ok: false, reason: "file_unreadable", error: "We couldn't read that file — try another photo." };
  }
}
